import { detectLang, parseDue } from "@letsdoit/due-parser";
import { useMemo, useState } from "react";
import type { Due } from "../api/types";
import { useT } from "../i18n";
import { colorHex, PRIORITY_COLORS } from "../lib/colors";
import { addDays, dueLabel, dueLocal, isoWeekday, monthGrid, monthName, weekdayName, weekStart } from "../lib/dates";
import { flattenTree, projectTree, sectionsOf, sortedLabels } from "../lib/tasks";
import { useSyncState } from "../store";
import {
  IconCalendar,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconFlag,
  IconHash,
  IconInbox,
  IconLabel,
} from "./icons";
import { usePrefs } from "./prefs";
import { type Anchor, Popover } from "./primitives";

export type DueChoice = { string: string } | { date: string } | null;

/** Natural-language date input (due-parser, en + zh), quick choices and a month calendar. */
export function DatePicker({
  anchor,
  onClose,
  value,
  onChange,
  allowRecurring = true,
}: {
  anchor: Anchor | null;
  onClose: () => void;
  value: Due | null;
  onChange: (due: DueChoice) => void;
  allowRecurring?: boolean;
}) {
  const t = useT();
  const prefs = usePrefs();
  const [text, setText] = useState("");
  const selectedDay = value ? dueLocal(value, prefs.tz).day : null;
  const [month, setMonth] = useState(() => (selectedDay ?? prefs.today).slice(0, 7));
  const parsed = useMemo(() => {
    if (!text.trim()) return null;
    const r = parseDue(
      { string: text, lang: detectLang(text) },
      { now: new Date(), userTimezone: prefs.tz, startDay: prefs.startDay },
    );
    return r;
  }, [text, prefs.tz, prefs.startDay]);
  const preview = parsed?.ok
    ? parsed.due
      ? `${dueLabel(parsed.due, prefs).text}${parsed.due.is_recurring ? ` · ${parsed.due.string}` : ""}`
      : t("noDate")
    : null;

  const choose = (c: DueChoice) => {
    onChange(c);
    onClose();
  };
  const weekend = (() => {
    const wd = isoWeekday(prefs.today);
    return addDays(prefs.today, wd >= 6 ? (wd === 6 ? 0 : 6) : 6 - wd);
  })();
  const nextWeek = addDays(weekStart(prefs.today, 1), 7);
  const [y, m] = month.split("-").map(Number) as [number, number];
  const grid = monthGrid(y, m, prefs.startDay);
  const shiftMonth = (d: number) => {
    const total = y * 12 + (m - 1) + d;
    setMonth(`${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}`);
  };
  const quick = [
    { key: "today", label: t("today"), day: prefs.today, cls: "tone-today" },
    { key: "tomorrow", label: t("tomorrow"), day: addDays(prefs.today, 1), cls: "tone-tomorrow" },
    { key: "weekend", label: t("thisWeekend"), day: weekend, cls: "tone-week" },
    { key: "nextweek", label: t("nextWeek"), day: nextWeek, cls: "tone-week" },
  ];
  return (
    <Popover anchor={anchor} onClose={onClose} className="date-picker">
      <input
        className="input dp-input"
        placeholder={t("typeDate")}
        value={text}
        autoFocus
        data-testid="date-input"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && parsed?.ok) {
            e.preventDefault();
            if (parsed.due && (allowRecurring || !parsed.due.is_recurring)) choose({ string: text });
            else if (!parsed.due) choose(null);
          }
        }}
      />
      {text && <div className="dp-preview">{preview ?? <span className="error-text">✕</span>}</div>}
      {quick.map((q) => (
        <button key={q.key} type="button" className="menu-item" onClick={() => choose({ date: q.day })}>
          <span className={q.cls}>
            <IconCalendar size={18} />
          </span>
          <span>{q.label}</span>
          <span className="muted small" style={{ marginLeft: "auto" }}>
            {weekdayName(q.day, prefs.lang, true)}
          </span>
        </button>
      ))}
      {value && (
        <button type="button" className="menu-item" onClick={() => choose(null)}>
          <IconCalendar size={18} />
          <span>{t("noDate")}</span>
        </button>
      )}
      <div className="menu-sep" />
      <div className="dp-cal">
        <div className="dp-cal-head">
          <span>{prefs.lang === "zh" ? `${y}年${m}月` : `${monthName(m, "en", true)} ${y}`}</span>
          <span>
            <button type="button" className="icon-btn" onClick={() => shiftMonth(-1)} aria-label={t("previous")}>
              <IconChevronLeft size={18} />
            </button>
            <button type="button" className="icon-btn" onClick={() => shiftMonth(1)} aria-label={t("next")}>
              <IconChevronRight size={18} />
            </button>
          </span>
        </div>
        <div className="dp-grid">
          {grid.slice(0, 7).map((d) => (
            <div key={`wd${d}`} className="wd">
              {weekdayName(d, prefs.lang, true).slice(0, prefs.lang === "zh" ? 2 : 1)}
            </div>
          ))}
          {grid.map((d) => {
            const cls = [
              "dp-day",
              d.slice(0, 7) !== month ? "other" : "",
              d < prefs.today ? "past" : "",
              d === prefs.today ? "today" : "",
              d === selectedDay ? "selected" : "",
            ].join(" ");
            return (
              <button key={d} type="button" className={cls} data-day={d} onClick={() => choose({ date: d })}>
                {Number(d.slice(8))}
              </button>
            );
          })}
        </div>
      </div>
    </Popover>
  );
}

export function PriorityMenu({
  anchor,
  onClose,
  value,
  onChange,
}: {
  anchor: Anchor | null;
  onClose: () => void;
  value: number;
  onChange: (p: number) => void;
}) {
  const t = useT();
  return (
    <Popover anchor={anchor} onClose={onClose}>
      {[4, 3, 2, 1].map((p) => (
        <button
          key={p}
          type="button"
          className="menu-item"
          onClick={() => {
            onChange(p);
            onClose();
          }}
        >
          <span style={{ color: PRIORITY_COLORS[p] }}>
            <IconFlag size={18} filled={p !== 1} />
          </span>
          <span>{t("priorityN", { n: 5 - p })}</span>
          {value === p && (
            <span className="check">
              <IconCheck size={16} />
            </span>
          )}
        </button>
      ))}
    </Popover>
  );
}

export function LabelPicker({
  anchor,
  onClose,
  value,
  onChange,
}: {
  anchor: Anchor | null;
  onClose: () => void;
  value: string[];
  onChange: (labels: string[]) => void;
}) {
  const state = useSyncState();
  const t = useT();
  const [q, setQ] = useState("");
  const labels = sortedLabels(state).map((l) => l.name);
  for (const v of value) if (!labels.includes(v)) labels.push(v);
  const shown = labels.filter((l) => l.toLowerCase().includes(q.toLowerCase()));
  const toggle = (name: string) => onChange(value.includes(name) ? value.filter((v) => v !== name) : [...value, name]);
  const exact = labels.some((l) => l.toLowerCase() === q.trim().toLowerCase());
  return (
    <Popover anchor={anchor} onClose={onClose}>
      <div style={{ padding: "4px 8px" }}>
        <input
          className="input"
          autoFocus
          value={q}
          placeholder={t("labels")}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && q.trim()) {
              e.preventDefault();
              toggle(shown[0] && exact ? shown[0] : q.trim());
              setQ("");
            }
          }}
        />
      </div>
      {shown.map((name) => {
        const label = Object.values(state.labels).find((l) => l.name === name);
        return (
          <button key={name} type="button" className="menu-item" onClick={() => toggle(name)}>
            <span style={{ color: colorHex(label?.color) }}>
              <IconLabel size={18} />
            </span>
            <span>{name}</span>
            {value.includes(name) && (
              <span className="check">
                <IconCheck size={16} />
              </span>
            )}
          </button>
        );
      })}
      {q.trim() && !exact && (
        <button
          type="button"
          className="menu-item"
          onClick={() => {
            toggle(q.trim());
            setQ("");
          }}
        >
          + {t("addLabel")}: “{q.trim()}”
        </button>
      )}
    </Popover>
  );
}

export interface ProjectTarget {
  projectId: string;
  sectionId: string | null;
}

export function ProjectPicker({
  anchor,
  onClose,
  value,
  onChange,
  withSections = true,
}: {
  anchor: Anchor | null;
  onClose: () => void;
  value: ProjectTarget | null;
  onChange: (target: ProjectTarget) => void;
  withSections?: boolean;
}) {
  const state = useSyncState();
  const t = useT();
  const [q, setQ] = useState("");
  const inbox = Object.values(state.projects).find((p) => p.inbox_project);
  const nodes = flattenTree(projectTree(Object.values(state.projects).filter((p) => !p.inbox_project)));
  const ql = q.toLowerCase();
  const pick = (target: ProjectTarget) => {
    onChange(target);
    onClose();
  };
  const row = (id: string, name: string, icon: React.ReactNode, depth: number) => (
    <div key={id}>
      {(!q || name.toLowerCase().includes(ql)) && (
        <button
          type="button"
          className="menu-item"
          style={{ paddingLeft: 12 + depth * 16 }}
          onClick={() => pick({ projectId: id, sectionId: null })}
        >
          {icon}
          <span>{name}</span>
          {value?.projectId === id && !value.sectionId && (
            <span className="check">
              <IconCheck size={16} />
            </span>
          )}
        </button>
      )}
      {withSections &&
        sectionsOf(state, id)
          .filter((s) => !q || s.name.toLowerCase().includes(ql) || name.toLowerCase().includes(ql))
          .map((s) => (
            <button
              key={s.id}
              type="button"
              className="menu-item"
              style={{ paddingLeft: 36 + depth * 16 }}
              onClick={() => pick({ projectId: id, sectionId: s.id })}
            >
              <span className="muted">/</span>
              <span>{s.name}</span>
              {value?.sectionId === s.id && (
                <span className="check">
                  <IconCheck size={16} />
                </span>
              )}
            </button>
          ))}
    </div>
  );
  return (
    <Popover anchor={anchor} onClose={onClose}>
      <div style={{ padding: "4px 8px" }}>
        <input
          className="input"
          autoFocus
          value={q}
          placeholder={t("project")}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {inbox && row(inbox.id, t("inbox"), <IconInbox size={18} />, 0)}
      {nodes.map((n) =>
        row(
          n.project.id,
          n.project.name,
          <span style={{ color: colorHex(n.project.color) }}>
            <IconHash size={18} />
          </span>,
          n.depth,
        ),
      )}
    </Popover>
  );
}
